import * as THREE from 'three';
import type { SfxName } from '../content/types';
import { clamp, damp } from '../core/math';
import type { Random } from '../core/Random';
import { animateDog, buildDog, type Dog } from './builders/animals';
import { buildCar, type BuiltCar } from './builders/vehicles';
import { inRect, rect } from './layout';
import { Traffic, type TrafficHit } from './Traffic';
import type { Sky } from './Sky';
import { glowTexture, tornadoTexture } from './textures';
import type { World } from './World';

interface Rocket {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  fuse: number;
  color: THREE.Color;
}

interface Patrol {
  car: BuiltCar;
  path: THREE.Vector3[];
  index: number;
  state: 'arrive' | 'parked' | 'leave';
  timer: number;
}

interface Chase {
  dog: Dog;
  x: number;
  z: number;
  heading: number;
  wanderTimer: number;
  barkTimer: number;
  speed: number;
  /** 1 = fresh. Drains while fleeing; at zero Peanut sits down to pant (your chance). */
  stamina: number;
  restTimer: number;
}

const FIREWORK_COLORS = ['#ff3b3b', '#ffffff', '#3b6bff', '#ffd23b', '#3bff7a', '#ff5bd6'];
const PARK_AREA = rect(-28, -44, 28, 40);

/** Transient, scripted things that happen in the world (weather, visitors, fireworks...). */
export class Effects {
  private tornado: { group: THREE.Group; layers: THREE.Mesh[]; t: number; duration: number } | null = null;
  private readonly rain: THREE.LineSegments;
  private rainAmount = 0;
  private stormTarget = 0;
  private readonly fireflies: THREE.Points;
  private readonly fireflyBase: Float32Array;
  private readonly rockets: Rocket[] = [];
  private readonly flash = new THREE.PointLight('#ffffff', 0, 60, 1.5);
  private readonly copLight = new THREE.PointLight('#ff2020', 0, 18, 1.5);
  private patrol: Patrol | null = null;
  private patrolCollider: number | null = null;
  private readonly traffic: Traffic;
  /** No rain or fireflies in the living room. */
  indoors = false;
  private chase: Chase | null = null;
  private shakeAmount = 0;
  private shakeTime = 0;
  readonly shakeOffset = new THREE.Vector3();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly world: World,
    private readonly sky: Sky,
    private readonly rng: Random,
    private readonly sfx: (name: SfxName) => void,
  ) {
    const drops = 1400;
    const pos = new Float32Array(drops * 6);
    for (let i = 0; i < drops; i++) {
      const x = rng.range(-35, 35);
      const y = rng.range(0, 30);
      const z = rng.range(-35, 35);
      pos.set([x, y, z, x + 0.15, y + 0.9, z + 0.1], i * 6);
    }
    const rg = new THREE.BufferGeometry();
    rg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.rain = new THREE.LineSegments(
      rg,
      new THREE.LineBasicMaterial({ color: '#aac4d6', transparent: true, opacity: 0 }),
    );
    this.rain.frustumCulled = false;
    this.rain.visible = false;
    scene.add(this.rain);

    const n = 70;
    this.fireflyBase = new Float32Array(n * 3);
    const fp = new Float32Array(n * 3);
    for (let i = 0; i < n; i++)
      this.fireflyBase.set([rng.range(-25, 25), rng.range(0.4, 2.6), rng.range(-25, 25)], i * 3);
    const fg = new THREE.BufferGeometry();
    fg.setAttribute('position', new THREE.BufferAttribute(fp, 3));
    this.fireflies = new THREE.Points(
      fg,
      new THREE.PointsMaterial({
        map: glowTexture(),
        color: '#d8ff6a',
        size: 0.35,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.fireflies.frustumCulled = false;
    scene.add(this.fireflies, this.flash, this.copLight);

    this.traffic = new Traffic(world.dynamic, rng);
  }

  get tornadoActive(): boolean {
    return this.tornado !== null;
  }

  get chaseTarget(): THREE.Vector3 | null {
    return this.chase ? new THREE.Vector3(this.chase.x, 0, this.chase.z) : null;
  }

  shake(intensity: number, seconds: number): void {
    this.shakeAmount = Math.max(this.shakeAmount, intensity);
    this.shakeTime = Math.max(this.shakeTime, seconds);
  }

  // --- tornado ----------------------------------------------------------------

  startTornado(): void {
    if (this.tornado) return;
    const group = new THREE.Group();
    const tex = tornadoTexture();
    const layers: THREE.Mesh[] = [];
    for (let i = 0; i < 4; i++) {
      const m = new THREE.MeshStandardMaterial({
        map: tex,
        color: i % 2 ? '#5d635c' : '#777d74',
        transparent: true,
        opacity: 0.75 - i * 0.1,
        side: THREE.DoubleSide,
        depthWrite: false,
        roughness: 1,
      });
      const geo = new THREE.CylinderGeometry(20 - i * 2.5, 1.8 + i * 0.6, 70, 18, 6, true);
      geo.translate(0, 35, 0);
      const layer = new THREE.Mesh(geo, m);
      layer.scale.setScalar(1 - i * 0.06);
      group.add(layer);
      layers.push(layer);
    }
    group.position.set(-170, 0, -100);
    this.scene.add(group);
    this.tornado = { group, layers, t: 0, duration: 55 };
    this.stormTarget = 1;
    this.sfx('tornado');
  }

  // --- cops ---------------------------------------------------------------------

  startCops(): void {
    if (this.patrol) return;
    const car = buildCar({ kind: 'cop', color: '#161616' });
    car.group.traverse((o) => (o.userData.noBatch = true));
    const path = [
      new THREE.Vector3(-150, 0, 63.5),
      new THREE.Vector3(-2, 0, 63.5),
      new THREE.Vector3(1.6, 0, 50),
      new THREE.Vector3(1.6, 0, 20),
      new THREE.Vector3(1.6, 0, 14.5),
    ];
    car.group.position.copy(path[0] as THREE.Vector3);
    this.world.dynamic.add(car.group);
    this.patrol = { car, path, index: 1, state: 'arrive', timer: 0 };
  }

  // --- fireworks ------------------------------------------------------------------

  launchFireworks(x: number, z: number, count = 6): void {
    for (let i = 0; i < count; i++) {
      this.rockets.push({
        x: x + this.rng.range(-0.5, 0.5),
        y: 0.5,
        z: z + this.rng.range(-0.5, 0.5),
        vx: this.rng.range(-1.5, 1.5),
        vy: this.rng.range(20, 27),
        vz: this.rng.range(-1.5, 1.5),
        fuse: this.rng.range(0.9, 1.5) + i * 0.35,
        color: new THREE.Color(this.rng.pick(FIREWORK_COLORS)),
      });
    }
    this.sfx('whistle');
  }

  private burst(r: Rocket): void {
    const n = 150;
    const second = new THREE.Color(this.rng.pick(FIREWORK_COLORS));
    for (let i = 0; i < n; i++) {
      const u = this.rng.range(-1, 1);
      const a = this.rng.range(0, Math.PI * 2);
      const s = Math.sqrt(1 - u * u);
      const speed = this.rng.range(8, 12);
      this.world.sparks.spawn({
        x: r.x,
        y: r.y,
        z: r.z,
        vx: Math.cos(a) * s * speed,
        vy: u * speed,
        vz: Math.sin(a) * s * speed,
        life: this.rng.range(1.4, 2.2),
        size: 0.85,
        color: i % 4 === 0 ? second : r.color,
        gravity: 4,
        drag: 1.1,
        grow: 0.4,
      });
    }
    this.flash.position.set(r.x, r.y, r.z);
    this.flash.color.copy(r.color);
    this.flash.intensity = 400;
    this.sfx('boom');
  }

  // --- lost dog chase --------------------------------------------------------------

  startChase(): void {
    if (this.chase) return;
    const dog = buildDog('chihuahua');
    const x = this.rng.range(-24, 24);
    const z = this.rng.range(-38, 34);
    dog.group.position.set(x, 0, z);
    this.world.dynamic.add(dog.group);
    this.chase = { dog, x, z, heading: 0, wanderTimer: 0, barkTimer: 1, speed: 0, stamina: 1, restTimer: 0 };
  }

  endChase(): void {
    if (!this.chase) return;
    this.chase.dog.group.removeFromParent();
    this.chase = null;
  }

  // --- update -------------------------------------------------------------------------

  /** Returns true if the player caught Peanut this frame. */
  update(
    dt: number,
    time: number,
    player: THREE.Vector3,
    camera: THREE.Camera,
    night: number,
  ): { caught: boolean } {
    let caught = false;

    // Weather.
    if (this.tornado) {
      const tw = this.tornado;
      tw.t += dt;
      const p = tw.t / tw.duration;
      tw.group.position.x = -170 + p * 340;
      tw.group.position.z = -100 + Math.sin(tw.t * 0.3) * 8;
      tw.layers.forEach((l, i) => {
        l.rotation.y += dt * (2.2 + i * 0.9);
        l.position.x = Math.sin(tw.t * 1.7 + i) * 1.5;
        const m = l.material as THREE.MeshStandardMaterial;
        m.map!.offset.y -= dt * 0.15;
      });
      if (this.rng.chance(dt * 30)) {
        const a = this.rng.range(0, Math.PI * 2);
        const r = this.rng.range(3, 14);
        this.world.smoke.spawn({
          x: tw.group.position.x + Math.cos(a) * r,
          y: this.rng.range(0, 25),
          z: tw.group.position.z + Math.sin(a) * r,
          vx: -Math.sin(a) * 14,
          vy: this.rng.range(2, 8),
          vz: Math.cos(a) * 14,
          life: 2,
          size: 2.5,
          grow: 2,
          color: '#4a4640',
        });
      }
      if (tw.t > tw.duration) {
        tw.group.removeFromParent();
        this.tornado = null;
        this.stormTarget = 0;
      }
      this.shake(0.12, 0.2);
    }
    this.sky.storm = damp(this.sky.storm, this.stormTarget, 0.6, dt);
    this.rainAmount = damp(this.rainAmount, this.stormTarget, 0.8, dt);
    this.rain.visible = this.rainAmount > 0.02 && !this.indoors;
    if (this.rain.visible) {
      (this.rain.material as THREE.LineBasicMaterial).opacity = this.rainAmount * 0.55;
      this.rain.position.set(camera.position.x, 0, camera.position.z);
      const pos = this.rain.geometry.attributes.position as THREE.BufferAttribute;
      const arr = pos.array as Float32Array;
      for (let i = 0; i < arr.length; i += 6) {
        arr[i + 1] = (arr[i + 1] as number) - dt * 26;
        arr[i + 4] = (arr[i + 4] as number) - dt * 26;
        if ((arr[i + 1] as number) < 0) {
          arr[i + 1] = (arr[i + 1] as number) + 30;
          arr[i + 4] = (arr[i + 4] as number) + 30;
        }
      }
      pos.needsUpdate = true;
    }

    // Fireflies on warm nights.
    const ffMat = this.fireflies.material as THREE.PointsMaterial;
    ffMat.opacity = this.indoors ? 0 : clamp((night - 0.55) * 2.2, 0, 1) * (1 - this.sky.storm);
    if (ffMat.opacity > 0) {
      const pos = this.fireflies.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const bx = this.fireflyBase[i * 3] as number;
        const by = this.fireflyBase[i * 3 + 1] as number;
        const bz = this.fireflyBase[i * 3 + 2] as number;
        const blink = Math.sin(time * 1.3 + i * 2.1) > 0.2 ? 0 : -50;
        pos.setXYZ(
          i,
          player.x + bx + Math.sin(time * 0.4 + i) * 1.5,
          by + Math.sin(time * 0.9 + i * 1.7) * 0.4 + blink,
          player.z + bz + Math.cos(time * 0.35 + i * 0.6) * 1.5,
        );
      }
      pos.needsUpdate = true;
    }

    // Fireworks.
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i] as Rocket;
      r.fuse -= dt;
      r.x += r.vx * dt;
      r.y += r.vy * dt;
      r.z += r.vz * dt;
      r.vy -= 6 * dt;
      this.world.sparks.spawn({
        x: r.x,
        y: r.y,
        z: r.z,
        vy: -2,
        life: 0.5,
        size: 0.22,
        color: '#ffcf7a',
        drag: 1,
      });
      if (r.fuse <= 0) {
        this.burst(r);
        this.rockets.splice(i, 1);
      }
    }
    this.flash.intensity = damp(this.flash.intensity, 0, 6, dt);

    // Highway traffic. Semis lay on the air horn if you're standing in their lane.
    if (this.traffic.update(dt, player).honk) this.sfx('semiHorn');

    this.updatePatrol(dt, time);
    if (this.chase) caught = this.updateChase(dt, time, player);

    // Camera shake.
    if (this.shakeTime > 0) {
      this.shakeTime -= dt;
      const a = this.shakeAmount;
      this.shakeOffset.set((Math.random() - 0.5) * a, (Math.random() - 0.5) * a, (Math.random() - 0.5) * a);
      if (this.shakeTime <= 0) this.shakeAmount = 0;
    } else this.shakeOffset.set(0, 0, 0);

    return { caught };
  }

  /** The highway vehicle touching a circle at (x, z), if any. */
  trafficHit(x: number, z: number, r: number): TrafficHit | null {
    return this.traffic.hit(x, z, r);
  }

  private updatePatrol(dt: number, time: number): void {
    const p = this.patrol;
    if (!p) {
      this.copLight.intensity = 0;
      return;
    }
    const g = p.car.group;
    const [red, blue] = p.car.lightBar!;
    const phase = Math.sin(time * 14) > 0;
    red.emissiveIntensity = phase ? 3 : 0.2;
    blue.emissiveIntensity = phase ? 0.2 : 3;
    this.copLight.color.set(phase ? '#ff2020' : '#2050ff');
    this.copLight.intensity = 30;
    this.copLight.position.set(g.position.x, 2.2, g.position.z);

    if (p.state === 'parked') {
      p.timer -= dt;
      if (p.timer <= 0) {
        p.state = 'leave';
        // Back out the way it came, but in the westbound lane.
        p.path = [...p.path].reverse().map((v) => (v.z === 63.5 ? new THREE.Vector3(v.x, 0, 58.5) : v));
        p.index = 1;
        if (this.patrolCollider !== null) this.world.collision.remove(this.patrolCollider);
        this.patrolCollider = null;
      }
      return;
    }
    const target = p.path[p.index] as THREE.Vector3;
    const dx = target.x - g.position.x;
    const dz = target.z - g.position.z;
    const dist = Math.hypot(dx, dz);
    const speed = p.index === 1 && p.state === 'arrive' ? 20 : 9;
    if (dist < 0.5) {
      p.index++;
      if (p.index >= p.path.length) {
        if (p.state === 'arrive') {
          p.state = 'parked';
          p.timer = 45;
          this.sfx('whoop');
          this.patrolCollider = this.world.collision.addRect(g.position.x, g.position.z, 2.1, 4.8);
        } else {
          g.removeFromParent();
          this.patrol = null;
        }
      }
      return;
    }
    const step = Math.min(dist, speed * dt);
    g.position.x += (dx / dist) * step;
    g.position.z += (dz / dist) * step;
    g.rotation.y = Math.atan2(-dz, dx);
    for (const w of p.car.wheels) w.rotation.y += (step / 0.34) * 1;
  }

  private updateChase(dt: number, time: number, player: THREE.Vector3): boolean {
    const c = this.chase as Chase;
    const dx = c.x - player.x;
    const dz = c.z - player.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 1.2) return true;
    let target = 1.4;
    if (c.restTimer > 0) {
      // Pooped out: sits there panting. Go get him.
      c.restTimer -= dt;
      target = 0;
      c.stamina = Math.min(1, c.stamina + dt * 0.2);
    } else if (dist < 7) {
      c.heading = Math.atan2(dx, dz) + Math.sin(time * 3) * 0.6;
      target = 3.4 + 2.4 * c.stamina;
      c.stamina = Math.max(0, c.stamina - dt * 0.22);
      if (c.stamina <= 0) {
        c.restTimer = 2.8;
        this.sfx('yip');
      }
    } else {
      c.stamina = Math.min(1, c.stamina + dt * 0.12);
      c.wanderTimer -= dt;
      if (c.wanderTimer <= 0) {
        c.wanderTimer = this.rng.range(1, 3);
        c.heading = this.rng.range(0, Math.PI * 2);
      }
    }
    c.speed = damp(c.speed, target, 6, dt);
    const pos = { x: c.x + Math.sin(c.heading) * c.speed * dt, z: c.z + Math.cos(c.heading) * c.speed * dt };
    if (!inRect(PARK_AREA, pos.x, pos.z)) {
      c.heading += Math.PI * 0.7;
      pos.x = clamp(pos.x, PARK_AREA.minX, PARK_AREA.maxX);
      pos.z = clamp(pos.z, PARK_AREA.minZ, PARK_AREA.maxZ);
    }
    if (this.world.collision.resolve(pos, 0.25)) c.heading += 0.9;
    c.x = pos.x;
    c.z = pos.z;
    c.dog.group.position.set(c.x, 0, c.z);
    c.dog.group.rotation.y = c.heading;
    animateDog(c.dog, time, c.speed, 1);
    c.barkTimer -= dt;
    if (c.barkTimer <= 0) {
      c.barkTimer = this.rng.range(1.2, 2.6);
      if (dist < 30) this.sfx('yip');
    }
    return false;
  }
}
